import client from '../client'

/**
 * Returns a paginated list of locations
 * @param {object} query location filtering options
 * @returns {Promise} resolves a paginated list of location objects
 */
export const getLocations = (query) => client.get('/locations', query)

/**
 * Returns a location object
 * @param {string} locationId Profitboss Location Id
 * @returns {Promise} resolves a location object
 */
export const getLocation = (locationId) =>
  client.get(`/locations/${locationId}`)

/**
 * Updates a location's attributes
 * @param {string} locationId Profitboss Location ID
 * @param {object} updatedSettings a set of updated location settings
 * @returns {Promise} resolves an updated location object
 */
export const updateLocation = (locationId, updatedSettings) =>
  client.patch(`/locations/${locationId}`, updatedSettings)

/**
 * A new Profitboss location
 * @param {object} location a Profitboss location object
 * @returns {Promise} resolves a location object
 */
export const createLocation = (location) => client.post('/locations', location)

/**
 * Links the Stripe account for the location
 * @param {string} locationId Profitboss location ID
 * @param {string} code Authorization code returned from Stripe
 * @returns {Promise} Resolves a location object
 */
export const linkStripeAccount = (locationId, code) =>
  client.post(`/locations/${locationId}/link-stripe`, { code })

/**
 * Update Funnel Links
 * @param {string} locationId location ID
 * @param {string} links Funnel link object
 */
export const updateFunnelLinks = (locationId, links) =>
  client.put(`/locations/${locationId}/funnel/links`, links)

/**
 * Set LocationId for square
 * @param {string} locationId Profitboss location ID
 * @param {string} squareLocationId Square Location Id
 */
export const updateSquareLocation = (locationId, squareLocationId) =>
  client.put(`/locations/${locationId}/square-location`, { squareLocationId })

/**
 * Set LocationId for otter
 * @param {string} locationId Profitboss location ID
 * @param {string} otterStoreId Otter Location Id
 */
export const updateOtterPosIntegration = (locationId, otterStoreId) =>
  client.put(`/locations/${locationId}/otter-store-id`, { otterStoreId })

/**
 * Sets Google location name
 * @param {object} query See below
 * @param {string} query.locationId Owner location ID
 * @param {string} query.listingName Google listing name
 */
export const linkGoogleBusinessLocation = ({ locationId, listingName }) =>
  client.put(`/locations/${locationId}/google`, { listingName })

/**
 * Remove POS integration
 * @param {string} locationId Profitboss location ID
 */
export const removePosIntegration = (locationId) =>
  client.delete(`/locations/${locationId}/pos`)

/**
 * Sync Google Business location
 * @param {object} query See Below
 * @param {string} query.locationId Owner location ID
 */
export const syncGoogleBusinessLocation = ({ locationId }) =>
  client.post(`/locations/${locationId}/google/sync`)

/**
 * Sets Google location name
 * @param {string} locationId Owner location ID
 */
export const unlinkGoogleBusinessLocation = (locationId) =>
  client.delete(`/locations/${locationId}/google`)

/**
 * Gets Google business info
 * @param {string} locationId Owner location ID
 */
export const getGoogleBusinessLocation = (locationId) =>
  client.get(`/locations/${locationId}/google/location`)

/**
 * Gets Google business location links
 * @param {string} locationId Owner location ID
 */
export const getGoogleBusinessLinks = async (locationId) => {
  try {
    return await client.get(`/locations/${locationId}/google/links`)
  } catch (err) {
    return []
  }
}

/**
 * Updates the Google My Business listing associated with the Owner location.
 * @param {string} locationId Owner location ID
 * @param {object} googleLocation Google location parameters
 * @param {string|null} googleLocation.title New Google title
 * @param {string|null} googleLocation.description New Google description
 * @param {string|null} googleLocation.websiteUri New website URI
 */
export const updateGoogleBusinessInformation = (
  locationId,
  { title, description, websiteUri }
) =>
  client.patch(`/locations/${locationId}/google`, {
    description,
    title,
    websiteUri,
  })

/**
 * Adds a link to the google listing of an owner location.
 * @param {string} locationId Owner location ID
 * @param {object} googleLink GMB link parameters
 * @param {string} googleLink.uri New Google title
 * @param {boolean} googleLink.isPreferred New Google description
 * @param {string} googleLink.placeActionType New website URI
 */
export const putGoogleLink = (
  locationId,
  { uri, isPreferred, placeActionType }
) =>
  client.put(`/locations/${locationId}/google/links`, {
    isPreferred,
    placeActionType,
    uri,
  })

/**
 * Updates a link from a Google listing of an owner location
 * @param {string} locationId Owner location ID
 * @param {object} googleLink GMB link parameters
 * @param {object} googleLink.linkName GMB link name (ID)
 * @param {string} googleLink.uri New Google title
 * @param {boolean} googleLink.isPreferred New Google description
 * @param {string} googleLink.placeActionType New website URI
 */
export const patchGoogleLink = (
  locationId,
  { linkName, uri, isPreferred, placeActionType }
) =>
  client.patch(`/locations/${locationId}/google/links`, {
    isPreferred,
    linkName,
    placeActionType,
    uri,
  })

/**
 * Deletes a link from a Google listing of an owner location
 * @param {string} locationId Owner location ID
 * @param {object} googleLink GMB link parameters
 * @param {object} googleLink.linkName GMB link name (ID)
 */
export const deleteGoogleLink = (locationId, { linkName }) =>
  client.delete(`/locations/${locationId}/google/links`, {
    linkName,
  })

/**
 * Gets payouts for a location's Stripe account
 * @param {string} locationId Owner location ID
 * @param {string|null} startingAfter ID of the first payout to return
 */
export const getPayouts = (locationId, startingAfter = undefined) =>
  client.get(`/locations/${locationId}/payouts`, { startingAfter })

/**
 * Gets balance transactions for a location's Stripe account
 * @param {string} locationId Owner location ID
 * @param {object} filter Filter balance transactions
 * @param {string|undefined} filter.payoutId Payout ID to filter
 */
export const getBalanceTransactions = (locationId, { payoutId } = {}) =>
  client.get(`/locations/${locationId}/payouts/balance-transactions`, {
    payoutId,
  })

/**
 * Gets a specific payout from a location's stripe account
 * @param {string} locationId Owner location ID
 * @param {string} payoutId Payout ID
 */
export const getPayout = (locationId, payoutId) =>
  client.get(`/locations/${locationId}/payouts/${payoutId}`)

/**
 * Gets a location's online health score & improvements
 *
 * Promise<{
 * score: number
 * improvements: {
 * slug: string
 * points: number
 * improvementPotential: number
 * }[]
 * }>
 * @param {string} locationId Owner location id
 * @returns {Promise} score and improvements (see comment)
 */
export const getLocationOnlineHealth = (locationId) =>
  client.get(`/locations/${locationId}/online-health`)

/**
 * Syncs a menu
 * @param {string} locationId Owner location id
 * @param {object} body Body data
 * @param {string} body.url url to sync from
 */
export const syncMenu = (locationId, body) =>
  client.post(`/locations/${locationId}/menu-sync`, body)

/**
 * Cancels A Menu Sync
 * @param {string} locationId Owner location id
 */
export const cancelMenuSync = (locationId) =>
  client.delete(`/locations/${locationId}/menu-sync`)

/**
 * Request marketing flyers for a location
 * @param {string} locationId Owner location id
 */
export const requestFlyers = (locationId) =>
  client.post(`/locations/${locationId}/flyers`)
